import { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, Wifi, WifiOff } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { useNotifications } from '../../contexts/NotificationContext';

const SEVERITY_COLORS = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
};

export default function NotificationBell() {
  const { notifications, unreadCount, connected, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      const now = new Date();
      const diff = now - d;
      if (diff < 60000) return 'Just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell-btn"
        style={{ cursor: 'pointer' }}
      >
        <Bell className="h-4.5 w-4.5 text-slate-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Inbox panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notification-inbox-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">Notifications</span>
              {connected ? (
                <Wifi className="h-3 w-3 text-emerald-500" data-testid="ws-connected-icon" />
              ) : (
                <WifiOff className="h-3 w-3 text-slate-400" data-testid="ws-disconnected-icon" />
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-1"
                data-testid="mark-all-read-btn"
                style={{ cursor: 'pointer' }}
              >
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400" data-testid="notification-empty">
                No notifications yet
              </div>
            ) : (
              notifications.map((n, i) => (
                <div
                  key={n.timestamp || i}
                  className={`px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-blue-50/40' : ''}`}
                  data-testid="notification-item"
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`h-2 w-2 rounded-full mt-1.5 flex-shrink-0 ${SEVERITY_COLORS[n.severity] || SEVERITY_COLORS.info}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-slate-800 truncate">{n.title}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-[9px] text-slate-400">{formatTime(n.timestamp)}</span>
                          {!n.read && (
                            <button
                              onClick={(e) => { e.stopPropagation(); markRead(n.timestamp); }}
                              className="text-slate-400 hover:text-blue-600"
                              data-testid="mark-read-btn"
                              style={{ cursor: 'pointer' }}
                            >
                              <Check className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{n.message}</p>
                      {n.event_type && (
                        <Badge variant="outline" className="mt-1 text-[8px]">{n.event_type.replace(/_/g, ' ')}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 text-center">
              <span className="text-[10px] text-slate-400">{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
